import React, { Fragment, ReactNode } from 'react';
import { common } from '@kurtosys/ksys-app-template';
import { action, computed, makeObservable, observable } from 'mobx';
import { observer } from 'mobx-react';

import { ComponentBase, InjectedStyledComponent, KEYS } from '../../../shared/index.js';
import ClickLayer from '../ClickLayer/ClickLayer.js';
import { IIconProps } from '../Icon/models/index.js';
import Image from '../Image/Image.js';
import { ITranslationContextValue } from '../Translate/models/index.js';
import Translate from '../Translate/Translate.js';
import VirtualizedList from '../VirtualizedList/VirtualizedList.js';

import DropdownHeader from './components/DropdownHeader/DropdownHeader.js';
import DropdownListItem from './components/DropdownListItem/DropdownListItem.js';
import { IDropdownItem } from './models/IDropdownItem.js';
import { IDropdownConfiguration, IDropdownProps, IDropdownState } from './models/index.js';
import ClearAllButton from './styledComponents/ClearAllButton.js';
import HeaderIcon from './styledComponents/HeaderIcon.js';
import HeaderItemIcon from './styledComponents/HeaderItemIcon.js';
import HeaderTitle from './styledComponents/HeaderTitle.js';
import HeaderTitleIcon from './styledComponents/HeaderTitleIcon.js';
import Label from './styledComponents/Label.js';
import List from './styledComponents/List.js';
import ListHeading from './styledComponents/ListHeading.js';
import ListItems from './styledComponents/ListItems.js';
import SearchInput from './styledComponents/SearchInput.js';
import SearchInputWrapper from './styledComponents/SearchInputWrapper.js';
import SelectAllButton from './styledComponents/SelectAllButton.js';
import Wrapper from './styledComponents/Wrapper.js';
import { DROPDOWN_CATEGORY_KEY, DROPDOWN_COMPONENT_KEY } from './Dropdown.meta.js';

const NAVIGATION_KEYS = [
	KEYS.ESCAPE,
	KEYS.UP_ARROW,
	KEYS.DOWN_ARROW,
	KEYS.PAGE_UP,
	KEYS.PAGE_DOWN,
	KEYS.HOME,
	KEYS.END,
];

const SPACE_KEYS = [KEYS.SPACE, KEYS.SPACEBAR];
const ACCEPT_KEYS = [KEYS.ENTER, ...SPACE_KEYS];

type TDropdownInputs = 'clearAllButton' | 'selectAllButton' | 'searchInput';

export const IdPrefix = 'react_dropdown_aria_';

export class Dropdown extends ComponentBase<IDropdownProps, IDropdownState, IDropdownConfiguration> {
	categoryKey = DROPDOWN_CATEGORY_KEY;
	componentKey = DROPDOWN_COMPONENT_KEY;

	labelId = `dropdown_label_${common.commonUtils.generateGuid()}`;
	headerId = `dropdown_header_${common.commonUtils.generateGuid()}`;
	listId = `dropdown_list_${common.commonUtils.generateGuid()}`;

	isOpen = false;
	renderReordered = false;
	searchTerm = '';
	searchInputFocused = false;
	clearAllButtonFocused = false;
	selectAllButtonFocused = false;
	selectedValues: string[] | null | undefined = null;
	pinnedSelectedItems: IDropdownItem[] = [];
	unselectedItems: IDropdownItem[] = [];
	focusedIndex = 0;
	focusHeader = false;

	translationContext: ITranslationContextValue | undefined;
	hasTranslationContext = false;

	constructor(props: IDropdownProps) {
		super(props);

		this.focusedIndex = props.items.findIndex((item) => item.focused);
		if (Array.isArray(props.selectedItem) && props.selectedItem.length > 0) {
			this.selectedValues = props.selectedItem.map((item) => item.value);
		}

		// Used to define the aria labelled by for external labels
		if (props.headerOptions?.labelOptions?.ariaLabelledBy) {
			this.labelId = props.headerOptions.labelOptions.ariaLabelledBy;
		}

		makeObservable(this, {
			isOpen: observable,
			renderReordered: observable,
			searchTerm: observable,
			searchInputFocused: observable,
			clearAllButtonFocused: observable,
			selectAllButtonFocused: observable,
			selectedValues: observable.ref,
			pinnedSelectedItems: observable.ref,
			unselectedItems: observable.ref,
			focusedIndex: observable.ref,
			focusHeader: observable.ref,
			setSearchInputFocus: action,
			setSelectAllFocus: action,
			setClearAllFocus: action,
			clearSearchInput: action,
			toggleSelectedValue: action,
			setFocusHeader: action,
			toggleOpen: action,
			handlePinSelected: action,
			closeDropdown: action,
			handleOnChange: action,
			handleDropdownSearchOnChange: action,
			selectedItemsFromProps: computed,
			selectedItems: computed,
			selectedDisplayItem: computed,
			placeholder: computed,
			singleItemDisplayOnly: computed,
			isMultiSelect: computed,
			dropdownAssets: computed,
			items: computed,
			selectSingleItem: action,
			isItemSelected: action,
			setSelectedValues: action,
			onNavigation: action,
			selectedItemId: computed,
			pinSelected: computed,
			itemsReorderedBySelection: computed,
			dropdownItems: computed,
			renderSearchInput: computed,
			renderSelectAllButton: computed,
			renderClearAllButton: computed,
			focusableItemsIndex: computed,
			focusedItemIndex: computed,
			currentItem: computed,
		});
	}

	setSearchInputFocus = (focused: boolean) => {
		this.searchInputFocused = focused;
	};

	setSelectAllFocus = (focused: boolean) => {
		this.selectAllButtonFocused = focused;
	};

	setClearAllFocus = (focused: boolean) => {
		this.clearAllButtonFocused = focused;
	};

	clearSearchInput = () => {
		this.searchTerm = '';
		this.searchInputFocused = false;
	};

	toggleSelectedValue = (value: string) => {
		let values: string[];
		if (this.isMultiSelect) {
			values = [...this.selectedItems.map((item) => item.value)];
			if (this.isValueSelected(value)) {
				values = values.filter((selectedValue) => selectedValue !== value);
			} else {
				values.push(value);
			}
		} else {
			values = [value];
		}

		this.selectedValues = values;
	};

	setFocusHeader = (value: boolean) => {
		this.focusHeader = value;
	};

	toggleOpen = () => {
		if (!this.singleItemDisplayOnly) {
			// Set the focused index to the first selected item or the first item in the list if none are selected
			const firstSelectedIndex = this.items.findIndex((item) => this.isItemSelected(item));
			this.focusedIndex = firstSelectedIndex >= 0 ? firstSelectedIndex : 0;
			this.isOpen = true;
		}
		this.clearSearchInput();
		this.handlePinSelected();
	};

	handlePinSelected = () => {
		if (this.pinSelected && this.selectedItems.length > 0 && this.isOpen) {
			// Set state for pin to top behavior
			this.renderReordered = true;
			this.pinnedSelectedItems = [...this.selectedItems];
			this.unselectedItems = [
				...this.items.filter(
					(item) =>
						// This prevents a deselected item to jump to original index until dropdown is closed/reopened
						!this.pinnedSelectedItems.find((pinnedItem) => pinnedItem.value === item.value) ||
						!this.isItemSelected(item),
				),
			];
		} else {
			// Cleanup: Reset the state to render the initial dropdown list
			this.renderReordered = false;
			this.pinnedSelectedItems = [];
			this.unselectedItems = [];
		}
	};

	closeDropdown = () => {
		this.isOpen = false;
		this.setFocusHeader(true);
		this.clearSearchInput();
		this.handlePinSelected();
	};

	handleOnChange = (
		e: React.MouseEvent<HTMLLIElement, MouseEvent> | React.ChangeEvent<HTMLInputElement> | undefined,
		value: string | undefined,
	) => {
		if (e) {
			e.preventDefault();
		}
		if (value) {
			const { onChange } = this.props;
			const items = this.items;
			this.toggleSelectedValue(value);
			if (!this.isMultiSelect) {
				this.toggleOpen();
			}
			if (!common.commonUtils.isNullOrEmpty(value) && onChange) {
				const index = items.findIndex((item) => item.value === value);
				onChange(this.isMultiSelect ? this.selectedItems : this.selectedItems[0], index);
			}
		}
	};

	handleDropdownSearchOnChange = (value: string): void => {
		this.searchTerm = value;
	};

	dropdownSearchCallback = (item: IDropdownItem): boolean => {
		const { searchInput } = this.props;

		const flags = searchInput && searchInput.caseSensitive ? 'g' : 'gi';
		const term = new RegExp(`${this.searchTerm}`, flags);

		return !!item.label.match(term) || false;
	};

	get selectedItemsFromProps(): IDropdownItem[] {
		const { selectedItem } = this.props;
		return this.convertToArray<IDropdownItem>(selectedItem);
	}

	get selectedItems(): IDropdownItem[] {
		const items = this.props.items;
		let selectedItems: IDropdownItem[] | undefined;
		if (items && items.length > 0) {
			if (this.selectedValues) {
				const isValueSelected = (value: string): boolean => {
					if (!common.commonUtils.isNullOrUndefined(this.selectedValues)) {
						return this.selectedValues.find((selectedValue) => selectedValue === value) !== undefined;
					}
					return false;
				};
				selectedItems = items.filter((item) => isValueSelected(item.value));
			}

			if (!selectedItems && this.selectedItemsFromProps.length > 0) {
				const isItemPreSelected = (item: IDropdownItem): boolean => {
					return (
						this.selectedItemsFromProps.find((selectedItem) => selectedItem.value === item.value) !==
						undefined
					);
				};
				selectedItems = items.filter((item) => isItemPreSelected(item));
			}
		}
		if (selectedItems && selectedItems.length > 1 && !this.isMultiSelect) {
			selectedItems = [selectedItems[0]];
		}
		return selectedItems || [];
	}

	get selectedDisplayItem(): IDropdownItem {
		const { selectedPlaceholder = '{selected} selected' } = this.props;
		const selectedStateItems = [...this.selectedItems];
		if (selectedStateItems.length === 0) {
			selectedStateItems.push(this.placeholder);
		}

		let label = '';
		let iconProps: IIconProps | undefined;
		const translationContext = this.hasTranslationContext && this.translationContext;

		if (selectedStateItems.length > 1) {
			const values = { selected: selectedStateItems.length };
			label = common.commonUtils.replacePlaceholders({ values, text: selectedPlaceholder });

			if (translationContext) {
				label = translationContext.translate(label);
			}
		} else {
			iconProps = selectedStateItems[0].iconProps;
			if (translationContext) {
				label = translationContext.translate(selectedStateItems[0].label);
			} else {
				label = selectedStateItems[0].label;
			}
		}

		return {
			label,
			iconProps,
			value: '',
		};
	}

	get placeholder(): IDropdownItem {
		let { placeholder } = this.props;
		if (this.translationContext && placeholder) {
			placeholder = this.translationContext.translate(placeholder);
		}
		return { value: '', label: placeholder } as IDropdownItem;
	}

	get singleItemDisplayOnly(): boolean {
		const { singleItemDisplayOnly = false, items = [] } = this.props;
		return singleItemDisplayOnly && items.length === 1;
	}

	get isMultiSelect(): boolean {
		const { isMultiSelect = false } = this.props;
		return isMultiSelect;
	}

	get dropdownAssets(): { caretUp: string; caretDown: string } {
		const caretUp = this.props && this.props.dropdownAssets && this.props.dropdownAssets.caretUp;
		const caretDown = this.props && this.props.dropdownAssets && this.props.dropdownAssets.caretDown;

		return {
			caretUp: (caretUp !== undefined ? caretUp : 'base.dropdown.caretUp') as string,
			caretDown: (caretDown !== undefined ? caretDown : 'base.dropdown.caretDown') as string,
		};
	}

	selectSingleItem() {
		const { items = [] } = this.props;
		if (items.length === 1) {
			this.selectedValues = [items[0].value];
		}
	}

	isItemSelected(item: IDropdownItem): boolean {
		return this.selectedItems.filter((selectedItem) => selectedItem.value === item.value).length > 0;
	}

	isValueSelected(value: string): boolean {
		return this.selectedItems.filter((selectedItem) => selectedItem.value === value).length > 0;
	}

	convertToArray<T>(values?: T | T[]): T[] {
		if (values) {
			if (!common.commonUtils.isArrayLike(values)) {
				return [values as T];
			}
			if (common.commonUtils.isArrayLike(values)) {
				return values as T[];
			}
		}
		return [];
	}
	/**
	 * Opposed to doing a JSON.stringify (which can cause circular reference), we compare values for change
	 */
	hasSelectedChanged(currSelected: IDropdownItem[], nextSelected: IDropdownItem[]) {
		if (currSelected.length !== nextSelected.length) {
			return true;
		}
		if (currSelected.length === nextSelected.length && currSelected.length > 0) {
			for (let i = 0; i < currSelected.length; i++) {
				const currItem = currSelected[i];
				if (nextSelected.find((nextItem) => nextItem.value === currItem.value) === undefined) {
					return true;
				}
			}
		}
		return false;
	}

	UNSAFE_componentWillReceiveProps(props: IDropdownProps) {
		// this logic allows a parent component to manage the selected values, opposed to the default internal state management
		const nextSelected = this.convertToArray<IDropdownItem>(props.selectedItem);
		const currSelected = this.convertToArray<IDropdownItem>(this.props.selectedItem);
		if (this.hasSelectedChanged(currSelected, nextSelected)) {
			this.setSelectedValues(nextSelected.map((item) => item.value));
		}
	}

	setSelectedValues = (selectedValues: string[] | undefined) => {
		this.selectedValues = selectedValues;
	};

	onNavigation(key: string) {
		const { pageKeyTraverseSize = 3 } = this.props;

		// Reset search onNavigation
		if (this.searchInputFocused) {
			this.setSearchInputFocus(false);
		}
		if (this.clearAllButtonFocused) {
			this.setClearAllFocus(false);
		}
		if (this.selectAllButtonFocused) {
			this.setSelectAllFocus(false);
		}

		switch (key) {
			case KEYS.UP_ARROW:
				if (this.isOpen) {
					if (this.focusedIndex === 0) {
						this.focusedIndex = this.focusableItemsIndex.length - 1;
					} else {
						this.focusedIndex = this.focusedIndex - 1;
					}
					if (this.focusedIndex < 0) {
						this.focusedIndex = this.focusableItemsIndex.length - 1;
					}
				} else {
					this.toggleOpen();
				}
				break;
			case KEYS.DOWN_ARROW:
				if (this.isOpen) {
					this.focusedIndex = (this.focusedIndex + 1) % this.focusableItemsIndex.length;
				} else {
					this.toggleOpen();
				}
				break;
			case KEYS.PAGE_UP:
				if (this.isOpen) {
					if (this.focusedIndex - pageKeyTraverseSize < 0 && this.focusedIndex !== 0) {
						this.focusedIndex = 0;
					} else if (this.focusedIndex - pageKeyTraverseSize < 0) {
						this.focusedIndex = this.focusableItemsIndex.length - 1;
					} else {
						this.focusedIndex = this.focusedIndex - pageKeyTraverseSize;
					}
				}
				break;
			case KEYS.PAGE_DOWN:
				if (this.isOpen) {
					if (this.focusedIndex === this.focusableItemsIndex.length - 1) {
						this.focusedIndex = 0;
					} else if (this.focusedIndex + pageKeyTraverseSize > this.focusableItemsIndex.length - 1) {
						this.focusedIndex = this.focusableItemsIndex.length - 1;
					} else {
						this.focusedIndex = (this.focusedIndex + pageKeyTraverseSize) % this.focusableItemsIndex.length;
					}
				}
				break;
			case KEYS.HOME:
				if (this.isOpen) {
					this.focusedIndex = 0;
				}
				break;
			case KEYS.END:
				if (this.isOpen) {
					this.focusedIndex = this.focusableItemsIndex.length - 1;
				}
				break;
			case KEYS.ESCAPE:
			case KEYS.TAB:
				this.closeDropdown();
				break;
			default:
				break;
		}
	}

	get focusableItemsIndex(): (number | TDropdownInputs)[] {
		const focusItemsIndex: (number | TDropdownInputs)[] = [];
		if (this.renderSearchInput !== null) {
			focusItemsIndex.push('searchInput');
		}
		for (let i = 0; i < this.items.length; i++) {
			focusItemsIndex.push(i);
		}
		if (this.renderSelectAllButton !== null) {
			focusItemsIndex.push('selectAllButton');
		}
		if (this.renderClearAllButton !== null) {
			focusItemsIndex.push('clearAllButton');
		}

		return focusItemsIndex;
	}

	get focusedItemIndex(): number | TDropdownInputs {
		return this.focusableItemsIndex[this.focusedIndex];
	}

	get currentItem(): IDropdownItem | undefined {
		const focusedItemIndex = this.focusableItemsIndex[this.focusedIndex];
		if (typeof focusedItemIndex === 'number') {
			return this.items[focusedItemIndex];
		}
		return undefined;
	}

	handleKeyDown = (event: React.KeyboardEvent<HTMLLIElement> | React.KeyboardEvent<HTMLDivElement>) => {
		const { clearAllButton, selectAllButton } = this.props.inputs || {};

		const { key } = event;
		if (NAVIGATION_KEYS.indexOf(key as KEYS) !== -1) {
			event.preventDefault();
			event.stopPropagation();
			this.onNavigation(key);
			// TODO: review this logic that seems to break when using up and down arrow keys on the clear all and select all buttons.
			// focus index is updating but focusing the element seems to stop and resets when index gets back to start or end of list
			if (this.focusedItemIndex === 'searchInput') {
				this.setSearchInputFocus(true);
			}
			if (this.focusedItemIndex === 'clearAllButton') {
				this.setClearAllFocus(true);
			}
			if (this.focusedItemIndex === 'selectAllButton') {
				this.setSelectAllFocus(true);
			}
			if (!this.isMultiSelect) {
				if (this.currentItem) {
					this.toggleSelectedValue(this.currentItem.value);
				}
			}
		} else if (ACCEPT_KEYS.indexOf(key as KEYS) !== -1 && !this.isOpen) {
			event.preventDefault();
			this.toggleOpen();
		} else if (key === KEYS.TAB) {
			this.closeDropdown();
		} else if (
			ACCEPT_KEYS.indexOf(key as KEYS) !== -1 &&
			this.items.length > 0 &&
			this.focusedIndex >= 0 &&
			this.isOpen
		) {
			if (!this.searchInputFocused && SPACE_KEYS.indexOf(key as KEYS) !== -1) {
				event.stopPropagation();
				event.preventDefault();
				// Handle additional dropdown elements keyboard events

				if (this.focusedItemIndex === 'clearAllButton' && clearAllButton) {
					clearAllButton.onClick && clearAllButton.onClick(event as any);
				} else if (this.focusedItemIndex === 'selectAllButton' && selectAllButton) {
					selectAllButton.onClick && selectAllButton.onClick(event as any);
				} else {
					this.handleOnChange(event as any, this.currentItem?.value);
				}
			}
		}
	};

	get selectedItemId() {
		return `${this.listId}_item_${this.focusedIndex}`;
	}

	get pinSelected(): boolean {
		const { pinSelected = false } = this.props;
		return pinSelected;
	}

	get items(): IDropdownItem[] {
		const { placeholder, includePlaceholderItem = false, items } = this.props;
		const filteredItems = [...items.filter(this.dropdownSearchCallback)];

		// Adds Placeholder
		if (includePlaceholderItem && !common.commonUtils.isNullOrEmpty(placeholder)) {
			filteredItems.unshift(this.placeholder);
		}

		return filteredItems.map((item) => ({ ...item, ref: React.createRef() }));
	}

	renderDropdownListItem = (item: IDropdownItem, index: number) => {
		const { checkboxPosition = 'left', iconPosition } = this.props;

		const listItemId = `${this.listId}_item_${item.value || index}`;
		item.focused = this.currentItem !== undefined && this.currentItem.value === item.value;

		return (
			<DropdownListItem
				tabIndex={0}
				id={listItemId}
				item={item}
				key={listItemId}
				onChange={(e: any) => this.handleOnChange(e, item.value)}
				onKeyDown={this.handleKeyDown}
				isMultiSelect={this.isMultiSelect}
				checkboxPosition={checkboxPosition}
				iconPosition={iconPosition}
				iconProps={item.iconProps}
				selected={this.isItemSelected(item)}
				isOpen={this.isOpen}
			></DropdownListItem>
		);
	};

	get itemsReorderedBySelection(): ReactNode[] {
		// because of the disasterous way that we handle selected items in this component, we need to force a render of the items
		// when the selected items change. By referencing the selectedItems here, we force a render when the selected items change.
		// This is a hack and should be fixed.
		this.selectedItems;
		return [...this.pinnedSelectedItems, ...this.unselectedItems]
			.filter(this.dropdownSearchCallback)
			.map(this.renderDropdownListItem);
	}

	get dropdownItems(): ReactNode[] {
		// because of the disasterous way that we handle selected items in this component, we need to force a render of the items
		// when the selected items change. By referencing the selectedItems here, we force a render when the selected items change.
		// This is a hack and should be fixed.
		this.selectedItems;
		return this.items.map(this.renderDropdownListItem);
	}

	get renderSearchInput(): React.ReactNode {
		const { searchInput } = this.props;

		if (searchInput) {
			if (!searchInput.enable) {
				return null;
			}

			return (
				<SearchInput
					tabIndex={1}
					value={this.searchTerm}
					onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
						this.handleDropdownSearchOnChange(event.target.value)
					}
					onKeyDown={this.handleKeyDown}
					placeholder={searchInput.placeholder || 'Filter items...'}
					focus={this.searchInputFocused}
				/>
			);
		}

		return null;
	}

	get renderSelectAllButton(): React.ReactNode {
		const { selectAllButton } = this.props.inputs || {};

		if (selectAllButton?.enable) {
			return (
				<SelectAllButton
					key={`select-all-button-${this.selectAllButtonFocused}`}
					hasFocus={this.selectAllButtonFocused}
					onClick={selectAllButton.onClick}
					onKeyDown={this.handleKeyDown}
				>
					{selectAllButton.text || 'Select all'}
				</SelectAllButton>
			);
		}

		return null;
	}

	get renderClearAllButton(): React.ReactNode {
		const { clearAllButton } = this.props.inputs || {};

		if (clearAllButton?.enable) {
			return (
				<ClearAllButton
					key={`clear-all-button-${this.clearAllButtonFocused}`}
					hasFocus={this.clearAllButtonFocused}
					onClick={clearAllButton.onClick}
					onKeyDown={this.handleKeyDown}
				>
					{clearAllButton.text || 'Clear all'}
				</ClearAllButton>
			);
		}

		return null;
	}

	render() {
		const {
			className,
			iconPosition,
			label,
			ariaControls,
			listHeading,
			headerOptions,
			placeholder,
			virtualize = false,
		} = this.props;

		const image = this.isOpen ? this.dropdownAssets.caretUp : this.dropdownAssets.caretDown;
		const headerItemIcon = <HeaderItemIcon {...this.selectedDisplayItem.iconProps} aria-hidden={'true'} />;
		// default to false if not defined for backwards compatibility
		const showButtonIcon = headerOptions?.iconOptions?.enable || false;
		const hideLabel = !label || headerOptions?.labelOptions?.hideLabel === true;
		// Need for backwards compatibility with global inputs which uses an external label for the dropdown
		const useInternalLabel = headerOptions?.labelOptions?.ariaLabelledBy === undefined;
		if (this.singleItemDisplayOnly && common.commonUtils.isNullOrEmpty(this.selectedValues)) {
			this.selectSingleItem();
		}

		// Used to force a render for the focus on key input
		this.focusedIndex;

		let listAriaProps = {};

		if (ariaControls) {
			listAriaProps = {
				'aria-controls': ariaControls,
			};
		}

		if (this.isOpen) {
			listAriaProps = {
				...listAriaProps,
				'aria-activedescendant': this.selectedItemId,
				'aria-controls': this.listId,
			};
		}

		return (
			<Wrapper className={className}>
				{useInternalLabel && (
					<Label id={this.labelId} value={label || placeholder || 'No Label Found'} hideLabel={hideLabel} />
				)}
				<DropdownHeader
					id={this.headerId}
					labelId={this.labelId}
					selectedItemId={this.selectedItemId}
					onClick={() => this.toggleOpen()}
					onKeyDown={(e: any) => this.handleKeyDown(e)}
					isOpen={this.isOpen}
					focus={this.focusHeader}
					onBlur={() => this.setFocusHeader(false)}
					{...listAriaProps}
				>
					<HeaderTitle>
						{iconPosition === 'left' && headerItemIcon}
						{showButtonIcon && <HeaderTitleIcon {...headerOptions?.iconOptions} />}
						<Translate>{this.selectedDisplayItem.label}</Translate>
						{iconPosition === 'right' && headerItemIcon}
					</HeaderTitle>

					{!this.singleItemDisplayOnly && (
						<HeaderIcon aria-hidden={'true'}>
							<Image asset={image} />
						</HeaderIcon>
					)}
				</DropdownHeader>

				{this.isOpen && (
					<Fragment>
						<ClickLayer onClick={this.closeDropdown} />
						<List>
							{listHeading && (
								<ListHeading>
									<Translate>{listHeading}</Translate>
								</ListHeading>
							)}
							<SearchInputWrapper>{this.renderSearchInput}</SearchInputWrapper>
							<ListItems id={this.listId} role={'listbox'}>
								{virtualize && virtualize.enable ? (
									<VirtualizedList
										{...virtualize.props}
										items={
											this.renderReordered ? this.itemsReorderedBySelection : this.dropdownItems
										}
									/>
								) : this.renderReordered ? (
									this.itemsReorderedBySelection
								) : (
									this.dropdownItems
								)}
							</ListItems>
							{this.renderSelectAllButton}
							{this.renderClearAllButton}
						</List>
					</Fragment>
				)}
			</Wrapper>
		);
	}
}

export default InjectedStyledComponent(observer(Dropdown), DROPDOWN_CATEGORY_KEY, DROPDOWN_COMPONENT_KEY);
